import {
    usePathPopulate,
    getAnchorCoordinates,
    getWidthProfileScale,
    getPlacementScaleRandomMultiplier,
    getPlacementPerpendicularScatterOffset,
    getCenterSizeFalloffMultiplier,
    getPlacementRotationDegrees,
    getRepeatedGradientPosition,
} from "./PathPopulateContext";
import { useMemo, type ReactNode } from "react";
import { getGradientColor, paletteColorForPathIndex } from "../../utils/svgUtils";
import { useSvgViewBoxZoom } from "../../hooks/useSvgViewBoxZoom";
import {
    paletteColorIndexForPlacement,
    randomHexColorForPlacement,
    type PathPopulateProgress,
} from "../../utils/pathPopulatePlacement";

// Helper function to apply fill color to SVG content
function applyFillToSvgContent(svgContent: string, fillColor: string): string {
    if (!svgContent) return "";

    // Use regex to find and replace fill attributes in all SVG elements
    // This will override existing fills and add fills to elements that don't have them
    // Match common SVG elements: path, circle, rect, ellipse, polygon, polyline, line
    // Handle both self-closing and regular tags
    const elementRegex =
        /<(path|circle|rect|ellipse|polygon|polyline|line)([^>]*?)(\s*\/)?>/gi;

    return svgContent.replace(
        elementRegex,
        (match, tagName, attributes, selfClose) => {
            // Remove existing fill attribute if present
            const cleanedAttributes = attributes.replace(
                /\s*fill\s*=\s*["'][^"']*["']/gi,
                "",
            );

            // Add new fill attribute
            const closing = selfClose ? " />" : ">";
            return `<${tagName}${cleanedAttributes} fill="${fillColor}"${closing}`;
        },
    );
}

function formatPathLengthUnits(n: number): string {
    if (!Number.isFinite(n) || n <= 0) return "0";
    if (n >= 1000) return n.toFixed(0);
    if (n >= 100) return n.toFixed(1);
    return n.toFixed(2);
}

const PROGRESS_PHASE_LABELS: Record<PathPopulateProgress["phase"], string> = {
    mount: "Loading path...",
    sample: "Sampling path",
    dedupe: "Removing duplicates",
    assign: "Assigning objects",
};

function formatProgress(info: PathPopulateProgress | null): string {
    if (!info) return "Computing placement...";
    const label = PROGRESS_PHASE_LABELS[info.phase];
    if (info.total <= 0) return `${label}...`;
    if (info.phase === "sample") {
        return `${label} (${info.processed + 1}/${info.total})`;
    }
    const pct = Math.min(
        100,
        Math.max(0, Math.round((info.processed / info.total) * 100)),
    );
    return `${label} (${pct}%)`;
}

interface FooterMetaInput {
    pathSvg: unknown;
    objectInnerSvgs: { content: string }[];
    pathLengthComputePending: boolean;
    placementComputePending: boolean;
    placementComputeProgress: PathPopulateProgress | null;
    estimatedPathLengthPx: number | null;
    pathSegmentCount: number;
    estimatedPlacementCount: number;
    placements: unknown[];
    hasGeneratedPlacementsForCurrentParams: boolean;
    gridEnabled: boolean;
}

function renderFooterMeta(input: FooterMetaInput): ReactNode {
    const {
        pathSvg,
        objectInnerSvgs,
        pathLengthComputePending,
        placementComputePending,
        placementComputeProgress,
        estimatedPathLengthPx,
        pathSegmentCount,
        estimatedPlacementCount,
        placements,
        hasGeneratedPlacementsForCurrentParams,
        gridEnabled,
    } = input;

    if (!pathSvg) return "Load object and path SVGs in the controls panel.";
    if (objectInnerSvgs.length === 0) {
        return "Load at least one object SVG to estimate placement.";
    }
    if (pathLengthComputePending) return "Measuring path length...";
    if (estimatedPathLengthPx == null) return "Could not measure path geometry.";
    if (pathSegmentCount === 0 && !placementComputePending) {
        return "No path geometry found in the path SVG.";
    }
    if (placementComputePending) {
        return `${formatProgress(placementComputeProgress)}`;
    }

    const pathInfo = (
        <>
            {" - "}est. path length{" "}
            <strong>{formatPathLengthUnits(estimatedPathLengthPx)}</strong>{" "}
            SVG units{" - "}
            {pathSegmentCount} segment{pathSegmentCount === 1 ? "" : "s"}
        </>
    );

    if (!hasGeneratedPlacementsForCurrentParams) {
        return (
            <>
                Estimated <strong>~{estimatedPlacementCount}</strong>{" "}
                instance{estimatedPlacementCount === 1 ? "" : "s"} at current
                spacing{gridEnabled ? " and grid snap" : ""}. Click{" "}
                <strong>Generate</strong> to place objects on the path.
                {pathInfo}
            </>
        );
    }

    return (
        <>
            <strong>{placements.length}</strong>{" "}
            instance{placements.length === 1 ? "" : "s"} on path{pathInfo}
        </>
    );
}

export function PathPopulateViewer() {
    const {
        pathSvg,
        placements,
        estimatedPlacementCount,
        hasGeneratedPlacementsForCurrentParams,
        generatePlacements,
        pathLengthComputePending,
        placementComputePending,
        placementComputeProgress,
        cancelPlacementCompute,
        objectInnerSvgs,
        viewBoxAttr,
        rotationMode,
        baseRotationDeg,
        polarRotationCenterXPct,
        polarRotationCenterYPct,
        scaleRandomness,
        perpendicularScatterPx,
        pathSegmentCount,
        estimatedPathLengthPx,
        canExport,
        handleDownload,
        showPaths,
        gridEnabled,
        showGrid,
        gridSize,
        anchor,
        scaleFactor,
        gradientEnabled,
        gradientMode,
        gradientColors,
        sourcePathColors,
        maxGradientSteps,
        gradientPerPathRepeats,
        widthProfileEnabled,
        widthProfile,
        widthProfileMode,
        widthProfileMinScale,
        centerSizeFalloffEnabled,
        centerSizeFalloffEdgeScale,
        pathViewBox,
    } = usePathPopulate();

    const base = useMemo(() => {
        const [x, y, w, h] = viewBoxAttr.split(/\s+/).map(Number);
        return { x, y, w, h };
    }, [viewBoxAttr]);

    const {
        svgRef,
        viewBoxAttr: viewBox,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    const polarRotationCenter = useMemo(() => {
        if (!pathViewBox) return null;
        const vb = pathViewBox.split(/\s+/).map(Number);
        if (
            vb.length !== 4 ||
            !vb.every((n) => Number.isFinite(n)) ||
            vb[2] <= 0 ||
            vb[3] <= 0
        ) {
            return null;
        }
        return {
            x: vb[0] + (vb[2] * polarRotationCenterXPct) / 100,
            y: vb[1] + (vb[3] * polarRotationCenterYPct) / 100,
        };
    }, [pathViewBox, polarRotationCenterXPct, polarRotationCenterYPct]);

    return (
        <div className="panel-viewer path-populate-viewer">
            <div className="path-tool-preview">
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomOut}
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomIn}
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={resetView}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                </div>
                <div className="svg-scroll-wrapper">
                    <div className="svg-container svg-container--fill">
                        <svg
                            ref={svgRef}
                            viewBox={viewBox}
                            xmlns="http://www.w3.org/2000/svg"
                            className="path-tool-svg"
                            style={{ touchAction: "none" }}
                            {...panHandlers}
                        >
                            {showGrid &&
                                (() => {
                                    const viewBoxParts = viewBoxAttr
                                        .split(/\s+/)
                                        .map(Number);
                                    if (viewBoxParts.length !== 4) return null;
                                    const [x, y, width, height] = viewBoxParts;
                                    const gridLines: JSX.Element[] = [];

                                    // Vertical lines
                                    for (
                                        let gx =
                                            Math.floor(x / gridSize) * gridSize;
                                        gx <= x + width;
                                        gx += gridSize
                                    ) {
                                        gridLines.push(
                                            <line
                                                key={`v-${gx}`}
                                                x1={gx}
                                                y1={y}
                                                x2={gx}
                                                y2={y + height}
                                                stroke="#666666"
                                                strokeWidth={0.5}
                                                opacity={0.3}
                                            />,
                                        );
                                    }

                                    // Horizontal lines
                                    for (
                                        let gy =
                                            Math.floor(y / gridSize) * gridSize;
                                        gy <= y + height;
                                        gy += gridSize
                                    ) {
                                        gridLines.push(
                                            <line
                                                key={`h-${gy}`}
                                                x1={x}
                                                y1={gy}
                                                x2={x + width}
                                                y2={gy}
                                                stroke="#666666"
                                                strokeWidth={0.5}
                                                opacity={0.3}
                                            />,
                                        );
                                    }

                                    return (
                                        <g className="grid-overlay">
                                            {gridLines}
                                        </g>
                                    );
                                })()}
                            {pathSvg && showPaths && (
                                <g
                                    className="path-outline"
                                    dangerouslySetInnerHTML={{
                                        __html: pathSvg.content.replace(
                                            /<\/?svg[^>]*>/g,
                                            "",
                                        ),
                                    }}
                                />
                            )}

                            {objectInnerSvgs.length > 0 &&
                                placements.map((p, idx) => {
                                    // Get the object SVG for this placement
                                    const objectSvg =
                                        objectInnerSvgs[p.objectIndex];
                                    if (!objectSvg) return null;

                                    // Compute per-placement scale
                                    const wpPosition =
                                        widthProfileMode === "per-path"
                                            ? p.pathPosition
                                            : p.globalPosition;
                                    const placementScale =
                                        (widthProfileEnabled
                                            ? scaleFactor *
                                              getWidthProfileScale(
                                                  wpPosition,
                                                  widthProfile,
                                                  widthProfileMinScale,
                                              )
                                            : scaleFactor) *
                                        (centerSizeFalloffEnabled
                                            ? getCenterSizeFalloffMultiplier(
                                                  p,
                                                  pathViewBox,
                                                  centerSizeFalloffEdgeScale,
                                              )
                                            : 1) *
                                        getPlacementScaleRandomMultiplier(
                                            p,
                                            idx,
                                            scaleRandomness,
                                        );

                                    // Calculate anchor offset based on this object's dimensions
                                    const anchorCoords =
                                        getAnchorCoordinates(anchor);
                                    let offsetX = 0;
                                    let offsetY = 0;
                                    if (objectSvg.dimensions) {
                                        offsetX =
                                            -anchorCoords.x *
                                            objectSvg.dimensions.width *
                                            placementScale;
                                        offsetY =
                                            -anchorCoords.y *
                                            objectSvg.dimensions.height *
                                            placementScale;
                                    }

                                    // Calculate fill color if enabled
                                    let svgContent = objectSvg.content;
                                    if (gradientEnabled) {
                                        if (
                                            gradientMode ===
                                            "random-per-instance"
                                        ) {
                                            const fillColor =
                                                randomHexColorForPlacement(
                                                    p.pathIndex,
                                                    idx,
                                                );
                                            svgContent =
                                                applyFillToSvgContent(
                                                    objectSvg.content,
                                                    fillColor,
                                                );
                                        } else if (
                                            gradientMode ===
                                                "random-palette-per-instance" &&
                                            gradientColors.length >= 1
                                        ) {
                                            const pi =
                                                paletteColorIndexForPlacement(
                                                    p.pathIndex,
                                                    idx,
                                                    gradientColors.length,
                                                );
                                            const fillColor =
                                                gradientColors[pi] ??
                                                gradientColors[0];
                                            svgContent =
                                                applyFillToSvgContent(
                                                    objectSvg.content,
                                                    fillColor,
                                                );
                                        } else if (
                                            gradientMode ===
                                                "random-per-path" &&
                                            gradientColors.length >= 1
                                        ) {
                                            const fillColor =
                                                paletteColorForPathIndex(
                                                    gradientColors,
                                                    p.pathIndex,
                                                );
                                            svgContent =
                                                applyFillToSvgContent(
                                                    objectSvg.content,
                                                    fillColor,
                                                );
                                        } else if (
                                            gradientMode === "source-path-color"
                                        ) {
                                            const fillColor =
                                                sourcePathColors[p.pathIndex];
                                            if (fillColor) {
                                                svgContent =
                                                    applyFillToSvgContent(
                                                        objectSvg.content,
                                                        fillColor,
                                                    );
                                            }
                                        } else if (
                                            (gradientMode === "per-path" ||
                                                gradientMode === "global") &&
                                            gradientColors.length >= 2
                                        ) {
                                            const position =
                                                gradientMode === "per-path"
                                                    ? getRepeatedGradientPosition(
                                                          p.pathPosition,
                                                          gradientPerPathRepeats,
                                                      )
                                                    : p.globalPosition;
                                            const gradientColor =
                                                getGradientColor(
                                                    gradientColors,
                                                    position,
                                                    maxGradientSteps,
                                                );
                                            svgContent =
                                                applyFillToSvgContent(
                                                    objectSvg.content,
                                                    gradientColor,
                                                );
                                        }
                                    }

                                    const scatter = getPlacementPerpendicularScatterOffset(
                                        p,
                                        idx,
                                        perpendicularScatterPx,
                                    );
                                    const angleDeg = getPlacementRotationDegrees({
                                        rotationMode,
                                        baseRotationDeg,
                                        placement: p,
                                        placementIndex: idx,
                                        polarCenter: polarRotationCenter,
                                        objectDimensions: objectSvg.dimensions,
                                        placementScale,
                                        anchor,
                                        scatter,
                                    });
                                    return (
                                        <g
                                            key={idx}
                                            transform={`translate(${p.x + offsetX + scatter.dx}, ${p.y + offsetY + scatter.dy}) rotate(${angleDeg}, ${-offsetX}, ${-offsetY}) scale(${placementScale})`}
                                            dangerouslySetInnerHTML={{
                                                __html: svgContent,
                                            }}
                                        />
                                    );
                                })}
                        </svg>
                    </div>
                </div>
                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {renderFooterMeta({
                            pathSvg,
                            objectInnerSvgs,
                            pathLengthComputePending,
                            placementComputePending,
                            placementComputeProgress,
                            estimatedPathLengthPx,
                            pathSegmentCount,
                            estimatedPlacementCount,
                            placements,
                            hasGeneratedPlacementsForCurrentParams,
                            gridEnabled,
                        })}
                    </p>
                    <div className="path-tool-footer-actions">
                        {placementComputePending ? (
                            <button
                                type="button"
                                className="generic-button"
                                onClick={cancelPlacementCompute}
                            >
                                Cancel
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="generic-button"
                                disabled={
                                    !pathSvg ||
                                    objectInnerSvgs.length === 0 ||
                                    pathLengthComputePending ||
                                    pathSegmentCount === 0 ||
                                    estimatedPathLengthPx == null ||
                                    estimatedPlacementCount === 0
                                }
                                onClick={generatePlacements}
                            >
                                {hasGeneratedPlacementsForCurrentParams
                                    ? "Regenerate"
                                    : "Generate"}
                            </button>
                        )}
                        <button
                            type="button"
                            className="generic-button"
                            disabled={!canExport}
                            onClick={handleDownload}
                        >
                            Download result SVG
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
